/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.ROLE.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/roles/RoleListPage.tsx. A distinct
 * presentation (dense card list + bottom-sheet detail) over the SAME data/logic
 * layer: reads via apiDb (csm_role_master + _tl, plus a csm_role_page_access
 * roll-up for the "pages granted" count), and the activate/deactivate write
 * routes through the Common Routine (routedUpdate) — §2. System roles cannot be
 * deactivated, mirroring the desktop rule. §16: never the desktop grid re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, ShieldCheck } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { buildMobileTranslationsGroup } from '../components/mobile_translations_group';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.ROLE.LIST';

interface MobileRoleRow {
  role_id: number;
  internal_code: string;
  is_system: 'Y' | 'N';
  is_active: 'Y' | 'N';
  display_text: string;
  view_pages: number;
  user_count: number;
  /** Every locale's text, kept so the related section can list them (§17.B14). */
  tl: TlRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }

export function MobileRoleListPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [roles, setRoles] = useState<MobileRoleRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileRoleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawRoles, error: rErr } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, is_system, is_active, csm_role_master_tl ( locale_code, display_text )')
        .order('internal_code');
      if (rErr) throw rErr;

      const today = new Date().toISOString().slice(0, 10);

      // Roll up (a) how many pages each role grants VIEW access to and (b) how many
      // users currently hold it. DEFECT FIX: the Super Administrator role reaches
      // every page via the admin override (csm_current_is_admin), NOT stored
      // csm_role_page_access rows — so the raw grant roll-up shows it as "0 pages".
      // Resolve that to the real count (all active pages) explicitly.
      const [rpaRes, uraRes, pagesRes] = await Promise.all([
        apiDb.from('csm_role_page_access').select('role_id, can_view'),
        apiDb.from('csm_user_role_assignment').select('role_id, effective_to'),
        apiDb.from('csm_page_master').select('page_id', { head: true, count: 'exact' }).eq('is_active', 'Y'),
      ]);
      if (rpaRes.error) throw rpaRes.error;
      if (uraRes.error) throw uraRes.error;

      const viewCounts: Record<number, number> = {};
      for (const a of (rpaRes.data ?? []) as { role_id: number; can_view: boolean }[]) {
        if (a.can_view) viewCounts[a.role_id] = (viewCounts[a.role_id] ?? 0) + 1;
      }
      const userCounts: Record<number, number> = {};
      for (const a of (uraRes.data ?? []) as { role_id: number; effective_to: string | null }[]) {
        if (a.effective_to == null || a.effective_to >= today) userCounts[a.role_id] = (userCounts[a.role_id] ?? 0) + 1;
      }
      const totalActivePages = pagesRes.count ?? 0;

      type RawRole = { role_id: number; internal_code: string; is_system: 'Y' | 'N'; is_active: 'Y' | 'N'; csm_role_master_tl?: TlRow[] };
      const rows: MobileRoleRow[] = ((rawRoles ?? []) as RawRole[]).map((r) => {
        const tls = r.csm_role_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const isSuperAdmin = r.internal_code === 'CSM.SUPER_ADMIN';
        return {
          role_id: r.role_id,
          internal_code: r.internal_code,
          is_system: r.is_system,
          is_active: r.is_active,
          tl: tls,
          display_text: exact?.display_text ?? fb?.display_text ?? r.internal_code,
          view_pages: isSuperAdmin ? totalActivePages : (viewCounts[r.role_id] ?? 0),
          user_count: userCounts[r.role_id] ?? 0,
        };
      });
      setRoles(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Lazy-load the selected role's children when the detail sheet opens: the users
  // who hold it, the pages it grants access to (V/A/C/D/S), and the job
  // descriptions that include it. Names resolved via _tl against the active locale.
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const roleId = selected.role_id;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const one = <T,>(v: T | T[] | undefined): T | undefined => (Array.isArray(v) ? v[0] : v);
      const pickTl = (tls: TlRow[] | undefined, fb: string) => {
        const list = tls ?? [];
        return list.find((x) => x.locale_code === activeLocale)?.display_text
          ?? list.find((x) => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? fb;
      };
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [ura, rpa, jdr] = await Promise.all([
          apiDb.from('csm_user_role_assignment')
            .select('user_id, effective_to, csm_user_master ( display_name, email )')
            .eq('role_id', roleId),
          apiDb.from('csm_role_page_access')
            .select('page_id, can_view, can_add, can_change, can_delete, can_signoff, csm_page_master ( internal_code, csm_page_master_tl ( locale_code, display_text ) )')
            .eq('role_id', roleId),
          apiDb.from('csm_job_description_role')
            .select('job_description_id, csm_job_description ( internal_code, csm_job_description_tl ( locale_code, display_text ) )')
            .eq('role_id', roleId),
        ]);

        type UraRow = { user_id: number; effective_to: string | null; csm_user_master?: { display_name: string; email: string } | { display_name: string; email: string }[] };
        const users: MobileChildItem[] = ((ura.data ?? []) as UraRow[])
          .filter((r) => r.effective_to == null || r.effective_to >= today)
          .map((r) => { const u = one(r.csm_user_master); return { id: r.user_id, label: u?.display_name ?? u?.email ?? `#${r.user_id}`, sub: u?.email }; })
          .sort(byLabel);

        type PageEmbed = { internal_code: string; csm_page_master_tl?: TlRow[] };
        type RpaRow = { page_id: number; can_view: boolean; can_add: boolean; can_change: boolean; can_delete: boolean; can_signoff: boolean; csm_page_master?: PageEmbed | PageEmbed[] };
        const pages: MobileChildItem[] = ((rpa.data ?? []) as RpaRow[]).map((r) => {
          const p = one(r.csm_page_master);
          const badges: string[] = [];
          if (r.can_view) badges.push('V');
          if (r.can_add) badges.push('A');
          if (r.can_change) badges.push('C');
          if (r.can_delete) badges.push('D');
          if (r.can_signoff) badges.push('S');
          return { id: r.page_id, label: pickTl(p?.csm_page_master_tl, p?.internal_code ?? `#${r.page_id}`), sub: p?.internal_code, badges };
        }).sort(byLabel);

        type JobEmbed = { internal_code: string; csm_job_description_tl?: TlRow[] };
        type JdrRow = { job_description_id: number; csm_job_description?: JobEmbed | JobEmbed[] };
        const jobs: MobileChildItem[] = ((jdr.data ?? []) as JdrRow[]).map((r) => {
          const j = one(r.csm_job_description);
          return { id: r.job_description_id, label: pickTl(j?.csm_job_description_tl, j?.internal_code ?? `#${r.job_description_id}`), sub: j?.internal_code };
        }).sort(byLabel);

        if (cancelled) return;
        setChildGroups([
          { key: 'users', table: 'csm_user_master', pkColumn: 'user_id', title: t('mobile.roles.related.users', 'Users'), items: users },
          { key: 'pages', table: 'csm_page_master', pkColumn: 'page_id', title: t('mobile.roles.related.pages', 'Pages with access'), items: pages },
          { key: 'jobs', table: 'csm_job_description', pkColumn: 'job_description_id', title: t('mobile.roles.related.job_descriptions', 'Job Descriptions'), items: jobs },
          buildMobileTranslationsGroup(selected.tl, t, activeLocale),
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return roles.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return r.display_text.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q);
    });
  }, [roles, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.roles.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const pagesLabel = (n: number) => t('mobile.roles.pages_count', { count: n, defaultValue: `${n} pages` });
  const usersLabel = (n: number) => t('mobile.roles.users_count', { count: n, defaultValue: `${n} users` });
  const typeLabel = (r: MobileRoleRow) =>
    r.is_system === 'Y' ? t('common.boolean.system', 'System') : t('common.boolean.custom', 'Custom');

  const toggleActive = async (r: MobileRoleRow) => {
    if (r.is_system === 'Y') return;
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_role_master', 'role_id', r.role_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const totalCount = roles.length;
  const activeCount = roles.filter((r) => r.is_active === 'Y').length;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.roles.title', 'Roles')}
        description={t('mobile.roles.description', 'Application roles and the pages and users assigned to each.')}
      />

      <MobileStatStrip
        stats={[
          { value: totalCount, label: t('mobile.roles.stat_total', 'Total') },
          { value: activeCount, label: t('common.boolean.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.roles.search', 'Search roles')}
          aria-label={t('mobile.roles.search', 'Search roles')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.roles.none', 'No roles match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.role_id}
              lead={<ShieldCheck />}
              leadTone={r.is_system === 'Y' ? 'blue' : 'slate'}
              title={r.display_text}
              subtitle={`${pagesLabel(r.view_pages)} · ${usersLabel(r.user_count)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.roles.detail', 'Role detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ShieldCheck size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.roles.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.roles.type', 'Type')} value={typeLabel(selected)} />
              <MobileDetailRow label={t('mobile.roles.pages', 'Pages (view)')} value={pagesLabel(selected.view_pages)} />
              <MobileDetailRow label={t('mobile.roles.users', 'Users')} value={usersLabel(selected.user_count)} />
            </MobileContentCard>
            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy || selected.is_system === 'Y'}
                title={selected.is_system === 'Y' ? t('mobile.roles.system_locked', 'System roles cannot be deactivated') : undefined}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
